import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api/client';
import type { Principal } from '@/lib/auth/principal';
import { usePinSession } from '@/lib/auth/pin/usePinSession';
import { Keypad } from './Keypad';

const PIN_LENGTH = 6;

interface PinLoginResponse {
  token: string;
  expires_at: string;
  must_change_pin: boolean;
  principal: Principal;
}

/**
 * Step 2 of the kiosk PIN flow. Big 6-digit keypad with shake-on-error.
 * On success: stores the PIN session and forwards to /pin-change (if forced)
 * or /dashboard.
 */
export function KioskPin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = usePinSession();
  const username = params.get('u') ?? '';
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) navigate('/kiosk/username', { replace: true });
  }, [username, navigate]);

  function pushDigit(d: string) {
    setError(null);
    setPin((p) => (p.length >= PIN_LENGTH ? p : p + d));
  }
  function backspace() {
    setError(null);
    setPin((p) => p.slice(0, -1));
  }

  useEffect(() => {
    if (pin.length !== PIN_LENGTH || busy) return;
    let cancelled = false;
    setBusy(true);
    apiFetch<PinLoginResponse>('/auth/pin-login', {
      method: 'POST',
      anonymous: true,
      body: JSON.stringify({ username, pin }),
    })
      .then((resp) => {
        if (cancelled) return;
        login({
          token: resp.token,
          expiresAt: resp.expires_at,
          mustChangePin: resp.must_change_pin,
          principal: resp.principal,
        });
        navigate(resp.must_change_pin ? '/pin-change' : '/dashboard', { replace: true });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setShake(true);
        setPin('');
        setError(e instanceof Error ? e.message : 'invalid credentials');
        setTimeout(() => setShake(false), 400);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pin, username, busy, login, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
      <img src="/favicon.svg" alt="Qargo Coffee" className="h-14 w-14" />
      <h2 className="text-xl font-semibold">Enter PIN for {username}</h2>

      <div
        className={`flex gap-2 ${shake ? 'animate-shake' : ''}`}
        style={shake ? { animation: 'kiosk-shake 0.3s', transform: 'translateX(0)' } : undefined}
      >
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div
            key={i}
            className={`w-10 h-12 border-2 rounded-md flex items-center justify-center text-2xl ${
              i < pin.length ? 'border-primary' : 'border-border'
            }`}
          >
            {i < pin.length ? '•' : ''}
          </div>
        ))}
      </div>

      <Keypad
        onDigit={pushDigit}
        onBackspace={backspace}
        onSubmit={() => undefined}
        submitLabel="…"
        disabled={busy}
      />

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <Link
        to="/kiosk/username"
        className="text-xs text-muted-foreground underline underline-offset-4"
      >
        ← Back
      </Link>

      <style>{`
        @keyframes kiosk-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
      `}</style>
    </div>
  );
}
